import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate, useParams } from 'react-router-dom'
import { FBPage } from '../../../components/FbUI/Page/FBPage'
import {
  FBPageHeader,
  FBPageInnerTabsContainer,
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
} from 'components/FbUI/Page/styles'
import { Tabs } from '../../../components/FbUI/Tabs'
import { SamplesTable } from '../../../components/Samples/SamplesTable'
import { SampleRequestFormModal } from '../../../components/Samples/SampleRequestFormModal'
import { SampleRequestPreviewModal } from '../../../components/Samples/SampleRequestPreviewModal'
import FbButton from '../../../components/FbUI/FbButton'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import apiService from '../../../services/api'
import { toast } from 'react-toastify'

export function SamplesDashboard() {
  const navigate = useNavigate()
  const params = useParams()
  const selectedTab = params?.type ?? 'all'
  const [searchParams, setSearchParams] = useSearchParams()
  const [showFormModal, setShowFormModal] = useState(false)
  const [editingSampleId, setEditingSampleId] = useState<number | null>(null)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewingSampleId, setPreviewingSampleId] = useState<number | null>(
    null
  )
  const [totalSamples, setTotalSamples] = useState<number | null>(null)
  const [totalMySamples, setTotalMySamples] = useState<number | null>(null)

  const api = apiService()
  const { data: companyData } = api.useGetCompanyDetails()

  // Check for sample_request_id in URL and sync modal state
  useEffect(() => {
    const sampleRequestIdParam = searchParams.get('sample_request_id')
    const mode = searchParams.get('mode')

    if (sampleRequestIdParam) {
      if (sampleRequestIdParam === 'new') {
        // Open new request modal (edit mode)
        setEditingSampleId(null)
        setShowFormModal(true)
        setShowPreviewModal(false)
        setPreviewingSampleId(null)
      } else {
        // Try to parse as sample ID
        const sampleId = parseInt(sampleRequestIdParam, 10)
        if (!isNaN(sampleId)) {
          if (mode === 'edit') {
            // Edit mode - open form modal
            setEditingSampleId(sampleId)
            setShowFormModal(true)
            setShowPreviewModal(false)
            setPreviewingSampleId(null)
          } else {
            // Default to preview mode
            setPreviewingSampleId(sampleId)
            setShowPreviewModal(true)
            setShowFormModal(false)
            setEditingSampleId(null)
          }
        }
      }
    } else {
      // No sample_request_id in URL, ensure modals are closed
      setShowFormModal(false)
      setEditingSampleId(null)
      setShowPreviewModal(false)
      setPreviewingSampleId(null)
    }
  }, [searchParams])

  const handleNewRequest = () => {
    // Check if CS email is configured
    if (!companyData?.sample_customer_service_email) {
      toast.error(
        'Please configure a Sample Customer Service Email in Settings → Company before creating sample requests.'
      )
      return
    }
    // Update URL - useEffect will handle opening modal
    setSearchParams({ sample_request_id: 'new' })
  }

  const handlePreviewRequest = (sampleId: number) => {
    // Update URL - useEffect will handle opening preview modal
    setSearchParams({ sample_request_id: sampleId.toString() })
  }

  const handleEditRequest = (sampleId: number) => {
    // Update URL with mode=edit - useEffect will handle opening edit modal
    setSearchParams({ sample_request_id: sampleId.toString(), mode: 'edit' })
  }

  const handleCloseModal = () => {
    // Remove sample_request_id and mode from URL - preserve other params
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('sample_request_id')
    newParams.delete('mode')
    setSearchParams(newParams)
  }

  const handleClosePreviewModal = () => {
    // Remove sample_request_id from URL - preserve other params
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('sample_request_id')
    setSearchParams(newParams)
  }

  return (
    <FBPage>
      <FBPageHeader>
        <FBPageTitleRow>
          <FBPageTitle>Samples</FBPageTitle>
          {!companyData?.sample_customer_service_email ? (
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id="sample-request-disabled-tooltip">
                  Configure Sample Customer Service Email in Settings → Company
                  to enable sample requests
                </Tooltip>
              }
            >
              <span style={{ marginLeft: 'auto' }}>
                <FbButton
                  disabled
                  style={{ width: 200, pointerEvents: 'none' }}
                >
                  Place New Request
                </FbButton>
              </span>
            </OverlayTrigger>
          ) : (
            <FbButton
              style={{ width: 200, marginLeft: 'auto' }}
              onClick={handleNewRequest}
            >
              Place New Request
            </FbButton>
          )}
        </FBPageTitleRow>
      </FBPageHeader>

      <FBPageInnerTabsContainer>
        <Tabs
          tabs={[
            {
              key: 'all',
              title: (
                <div>
                  <b>All Samples</b> {totalSamples ? `(${totalSamples})` : ''}
                </div>
              ),
            },
            {
              key: 'my',
              title: (
                <div>
                  <b>My Samples</b>{' '}
                  {totalMySamples ? `(${totalMySamples})` : ''}
                </div>
              ),
            },
          ]}
          selectedTab={selectedTab}
          setSelectedTab={(value) => {
            navigate(`/samples/${value}/`)
          }}
          tabStyle={{ width: '180px' }}
        />
      </FBPageInnerTabsContainer>

      <FBPageTableContainer
        style={{
          height: 'calc(var(--base-page-table-height) - 95px)',
        }}
      >
        {selectedTab === 'all' && (
          <SamplesTable
            tableKey="all-samples-table"
            onEditRequest={handleEditRequest}
            onPreviewRequest={handlePreviewRequest}
            setTotalRowsCount={setTotalSamples}
          />
        )}
        {selectedTab === 'my' && (
          <SamplesTable
            tableKey="my-samples-table"
            onEditRequest={handleEditRequest}
            onPreviewRequest={handlePreviewRequest}
            setTotalRowsCount={setTotalMySamples}
            myRequestsOnly
          />
        )}
      </FBPageTableContainer>

      {showFormModal && (
        <SampleRequestFormModal
          show={showFormModal}
          onHide={handleCloseModal}
          sampleRequestId={editingSampleId}
        />
      )}

      {showPreviewModal && (
        <SampleRequestPreviewModal
          show={showPreviewModal}
          onHide={handleClosePreviewModal}
          sampleRequestId={previewingSampleId}
        />
      )}
    </FBPage>
  )
}
